import React, { useState, useEffect } from "react";
import { Card, Select, Form, Button, App } from "antd";
import { useAuth } from "../../context/AuthContext";
import { analysisApi } from "../../api/analysis";

const { Option } = Select;

interface AIModel {
  value: string;
  label: string;
  description: string;
}

interface SettingsFormValues {
  aiModel: string;
}

function SettingsPage() {
  const [form] = Form.useForm();
  const { user, updateUserSettings } = useAuth();
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const { message } = App.useApp();

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const availableModels = await analysisApi.getAvailableModels();
        setModels(availableModels.data);

        if (user?.settings?.preferredModel) {
          form.setFieldValue("aiModel", user.settings.preferredModel);
        }
      } catch (error) {
        message.error("Failed to load AI models");
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, [form, user, message]);

  const onFinish = async (values: SettingsFormValues) => {
    try {
      await updateUserSettings({ preferredModel: values.aiModel });
      message.success("Settings updated successfully");
      window.dispatchEvent(new Event("settingsUpdated"));
    } catch (error: any) {
      console.error("Settings update error:", error);
      message.error(
        error.response?.data?.detail || "Failed to update settings"
      );
    }
  };

  return (
    <Card title="User Settings" loading={loading}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          aiModel: user?.settings?.preferredModel || "gpt2",
        }}
      >
        <Form.Item
          name="aiModel"
          label="Preferred AI Model"
          rules={[{ required: true, message: "Please select an AI model" }]}
        >
          <Select placeholder="Select AI model">
            {models.map((model) => (
              <Option
                key={model.value}
                value={model.value}
                title={model.description}
              >
                {model.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Button type="primary" htmlType="submit">
            Save Settings
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

export default SettingsPage;
